import React from 'react';
import { Clock, FileText, Pencil, Square, Upload, Video, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const sectionLabel = 'text-[10px] uppercase tracking-widest text-white/50 mb-1.5';
const chip = (on) => `h-9 px-3 text-xs font-bold rounded-md transition-all ${on ? 'bg-amber-400 text-black' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`;
const outline = 'border-white/20 text-white hover:bg-white/10';

function QuarterSection({ quarter, onChangeQuarter }) {
  return (
    <div>
      <div className={sectionLabel}>Quarter</div>
      <div className="flex gap-1.5 flex-wrap">
        {[1, 2, 3, 4, 5].map(q => (
          <button key={q} onClick={() => onChangeQuarter(q)} className={chip(quarter === q)} data-testid={`menu-quarter-${q}`}>
            {q === 5 ? 'OT' : `Q${q}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function LengthSection({ quarterLengthSec, onQuickLength }) {
  const current = Math.round((quarterLengthSec || 600) / 60);
  return (
    <div>
      <div className={`${sectionLabel} flex items-center gap-1.5`}>
        <Clock className="w-3 h-3" /> Quarter Length
      </div>
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="menu-quarter-length">
        {[8, 10, 12].map(m => (
          <button key={m} onClick={() => onQuickLength(m)} className={chip(current === m)} data-testid={`menu-length-${m}`}>
            {m}:00
          </button>
        ))}
        <span className="text-[10px] text-white/40 ml-1">resets clock</span>
      </div>
    </div>
  );
}

function LineupSection({ teamA, teamB, onEditLineup, onEditRoster }) {
  return (
    <div>
      <div className={sectionLabel}>Lineups</div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" onClick={() => onEditLineup('a')} className={outline} data-testid="menu-edit-lineup-a">
          Edit {teamA.name}
        </Button>
        <Button variant="outline" size="sm" onClick={() => onEditLineup('b')} className={outline} data-testid="menu-edit-lineup-b">
          Edit {teamB.name}
        </Button>
      </div>
      <Button variant="outline" size="sm" onClick={onEditRoster} className={`w-full mt-2 ${outline}`} data-testid="menu-edit-roster-btn">
        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Players / Played (DNP)
      </Button>
    </div>
  );
}

function ToolsSection({ onBoxScore, onVideo, onImport }) {
  return (
    <div className="grid grid-cols-2 gap-2 pt-1">
      <Button variant="outline" size="sm" onClick={onBoxScore}
        className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10 col-span-2"
        data-testid="menu-boxscore-btn">
        <FileText className="w-3.5 h-3.5 mr-1.5" /> Box Score (live)
      </Button>
      <Button variant="outline" size="sm" onClick={onVideo} className={outline} data-testid="menu-video-btn">
        <Video className="w-3.5 h-3.5 mr-1.5" /> Video URL
      </Button>
      <Button variant="outline" size="sm" onClick={onImport} className={outline} data-testid="menu-import-btn">
        <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
      </Button>
    </div>
  );
}

// Game Settings drawer. Actions that open another screen close the drawer first.
export function GameSettingsMenu({
  quarter, quarterLengthSec, teamA, teamB, onClose, onChangeQuarter, onQuickLength,
  onEditLineup, onEditRoster, onBoxScore, onVideo, onImport, onEndGame,
}) {
  const closeThen = (fn) => (...args) => { onClose(); fn(...args); };
  return (
    <div className="fixed inset-0 z-[105] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="menu-overlay">
      <div className="w-full max-w-md bg-[#001A5C] rounded-t-2xl sm:rounded-2xl border border-white/10 p-5 m-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Game Settings</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="menu-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <QuarterSection quarter={quarter} onChangeQuarter={onChangeQuarter} />
          <LengthSection quarterLengthSec={quarterLengthSec} onQuickLength={onQuickLength} />
          <LineupSection teamA={teamA} teamB={teamB} onEditLineup={closeThen(onEditLineup)} onEditRoster={closeThen(onEditRoster)} />
          <ToolsSection onBoxScore={closeThen(onBoxScore)} onVideo={closeThen(onVideo)} onImport={onImport} />
          <Button onClick={onEndGame} className="w-full bg-rose-600 hover:bg-rose-500 text-white" data-testid="menu-end-game-btn">
            <Square className="w-4 h-4 mr-2" /> End Game
          </Button>
        </div>
      </div>
    </div>
  );
}
